import {
  Component,
  computed,
  DestroyRef,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { combineLatest } from 'rxjs';
import { ButtonModule } from 'primeng/button';
import { Card } from 'primeng/card';
import { ChartModule } from 'primeng/chart';
import { InputText } from 'primeng/inputtext';
import { Message } from 'primeng/message';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { TableModule } from 'primeng/table';
import { Tag } from 'primeng/tag';
import { TooltipModule } from 'primeng/tooltip';
import { WebAnalyticsService } from '../../services/web-analytics.service';
import { WebAnalyticsUiService } from '../../services/web-analytics-ui.service';
import {
  WebAnalyticsPathCount,
  WebAnalyticsUserDetailData,
  WebAnalyticsUserEventRow,
  WebAnalyticsUserSession,
} from '../../models/web-analytics-dashboard';
import { NexusUserProfile } from '../../utils/nexus-user-profile';
import { dashboardFiltersFromQuery } from '../../utils/web-analytics-route-sync';
import { humanizeAnalyticsPath, truncatePathMiddle } from '../../utils/path-labels';
import { CountryLabelComponent } from '../../components/country-label/country-label.component';
import { getChartColors } from '../../theme/design-tokens';

@Component({
  selector: 'app-web-analytics-user-detail',
  imports: [
    CommonModule,
    RouterLink,
    FormsModule,
    ButtonModule,
    Card,
    ChartModule,
    InputText,
    Message,
    ProgressSpinnerModule,
    TableModule,
    Tag,
    TooltipModule,
    CountryLabelComponent,
  ],
  templateUrl: './web-analytics-user-detail.component.html',
  styleUrl: './web-analytics-user-detail.component.scss',
})
export class WebAnalyticsUserDetailComponent implements OnInit {
  private readonly webAnalytics = inject(WebAnalyticsService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);
  readonly waUi = inject(WebAnalyticsUiService);

  readonly loading = signal(false);
  readonly error = signal<string | null>(null);
  readonly detail = signal<WebAnalyticsUserDetailData | null>(null);
  readonly nexusProfile = signal<NexusUserProfile | null>(null);
  readonly nexusProfileLoading = signal(false);

  readonly activityData = signal<Record<string, unknown> | null>(null);
  readonly activityOptions = signal<Record<string, unknown> | null>(null);
  readonly typeBarData = signal<Record<string, unknown> | null>(null);
  readonly typeBarOptions = signal<Record<string, unknown> | null>(null);

  readonly expandedSessions = signal<Set<string>>(new Set());

  /** Raw events accumulated across cursor pages ("load more"). */
  readonly accumulatedEvents = signal<WebAnalyticsUserEventRow[]>([]);
  readonly nextCursor = signal<string | null>(null);
  readonly hasMore = signal(false);
  readonly loadingMore = signal(false);

  /** Sessions rebuilt from all loaded events; grows as more pages load. */
  readonly sessions = computed<WebAnalyticsUserSession[]>(() =>
    this.groupSessions(this.accumulatedEvents()),
  );
  readonly topPaths = computed<WebAnalyticsPathCount[]>(() =>
    this.computeTopPaths(this.accumulatedEvents()),
  );

  eventsFilterType = '';
  eventsFirst = 0;
  /** Display page size in the local paginator. */
  readonly eventsPageSize = 25;
  /** Events fetched per cursor page from the server. */
  private readonly eventsPageFetch = 500;

  ngOnInit(): void {
    combineLatest([this.route.paramMap, this.route.queryParamMap])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.eventsFirst = 0;
        this.eventsFilterType = '';
        this.load();
      });
  }

  toggleSession(id: string): void {
    const s = new Set(this.expandedSessions());
    if (s.has(id)) {
      s.delete(id);
    } else {
      s.add(id);
    }
    this.expandedSessions.set(s);
  }

  sessionOpen(id: string): boolean {
    return this.expandedSessions().has(id);
  }

  humanPath(p: string): string {
    return humanizeAnalyticsPath(p).label;
  }

  truncPath(p: string): string {
    return truncatePathMiddle(p, 64);
  }

  backToUsers(): void {
    void this.router.navigate(['/users'], {
      queryParams: this.route.snapshot.queryParams,
    });
  }

  navigateFilterPath(path: string): void {
    const f =
      dashboardFiltersFromQuery(this.route.snapshot.queryParamMap) ?? undefined;
    if (!f) {
      return;
    }
    const username = this.route.snapshot.paramMap.get('username') ?? '';
    void this.router.navigate(['/'], {
      queryParams: {
        ...this.route.snapshot.queryParams,
        filterPath: path,
        filterUsername: decodeURIComponent(username),
      },
    });
  }

  onEventsPage(event: { first?: number }): void {
    // Client-side pagination only; just remember the offset for state restoration.
    this.eventsFirst = event.first ?? 0;
  }

  applyEventsFilter(): void {
    this.eventsFirst = 0;
    this.load();
  }

  /** Total events loaded so far vs. the user's full total (from rollups). */
  loadedSummary(): { loaded: number; total: number } {
    return {
      loaded: this.accumulatedEvents().length,
      total: this.detail()?.eventsPage.totalCount ?? this.accumulatedEvents().length,
    };
  }

  eventSeverity(
    t: string,
  ): 'success' | 'info' | 'warn' | 'secondary' | 'danger' | 'contrast' {
    const x = (t ?? '').toLowerCase();
    if (x.includes('error') || x.includes('fail')) {
      return 'danger';
    }
    if (x === 'page_view') {
      return 'info';
    }
    if (x === 'api_request') {
      return 'success';
    }
    return 'secondary';
  }

  private load(): void {
    const usernameEnc = this.route.snapshot.paramMap.get('username') ?? '';
    const username = decodeURIComponent(usernameEnc);
    const f = dashboardFiltersFromQuery(this.route.snapshot.queryParamMap);
    if (!f?.startDate || !f.endDate || !username) {
      this.error.set('Missing date range or username.');
      return;
    }
    this.loading.set(true);
    this.error.set(null);
    this.nexusProfile.set(null);
    this.accumulatedEvents.set([]);
    this.nextCursor.set(null);
    this.hasMore.set(false);
    this.eventsFirst = 0;
    this.loadNexusProfile(username);
    this.webAnalytics
      .getUserDetail({
        filters: f,
        username,
        eventsLimit: this.eventsPageFetch,
        eventsCursor: null,
        eventsFilterEventType: this.eventsFilterType.trim(),
      })
      .subscribe({
        next: (d) => {
          this.detail.set(d);
          if (d) {
            this.accumulatedEvents.set([...d.eventsPage.events]);
            this.nextCursor.set(d.eventsPage.nextCursor || null);
            this.hasMore.set(d.eventsPage.hasMore);
            this.applyCharts(d);
          } else {
            this.activityData.set(null);
            this.typeBarData.set(null);
          }
          this.loading.set(false);
        },
        error: (err: unknown) => {
          this.error.set(
            err instanceof Error ? err.message : 'Could not load user.',
          );
          this.loading.set(false);
        },
      });
  }

  private loadNexusProfile(username: string): void {
    this.nexusProfileLoading.set(true);
    this.webAnalytics.getNexusUserProfile(username).subscribe({
      next: (profile) => {
        this.nexusProfile.set(profile);
        this.nexusProfileLoading.set(false);
      },
      error: () => {
        this.nexusProfileLoading.set(false);
      },
    });
  }

  loadMore(): void {
    const cursor = this.nextCursor();
    if (!cursor || this.loadingMore() || !this.hasMore()) {
      return;
    }
    const usernameEnc = this.route.snapshot.paramMap.get('username') ?? '';
    const username = decodeURIComponent(usernameEnc);
    const f = dashboardFiltersFromQuery(this.route.snapshot.queryParamMap);
    if (!f?.startDate || !f.endDate || !username) {
      return;
    }
    this.loadingMore.set(true);
    this.webAnalytics
      .getUserDetail({
        filters: f,
        username,
        eventsLimit: this.eventsPageFetch,
        eventsCursor: cursor,
        eventsFilterEventType: this.eventsFilterType.trim(),
      })
      .subscribe({
        next: (d) => {
          if (d) {
            this.accumulatedEvents.update((prev) => [
              ...prev,
              ...d.eventsPage.events,
            ]);
            this.nextCursor.set(d.eventsPage.nextCursor || null);
            this.hasMore.set(d.eventsPage.hasMore);
          }
          this.loadingMore.set(false);
        },
        error: (err: unknown) => {
          this.error.set(
            err instanceof Error ? err.message : 'Could not load more events.',
          );
          this.loadingMore.set(false);
        },
      });
  }

  private groupSessions(
    events: WebAnalyticsUserEventRow[],
  ): WebAnalyticsUserSession[] {
    const groups = new Map<string, WebAnalyticsUserEventRow[]>();
    for (const ev of events) {
      const sid = (ev.sessionId ?? '').trim();
      if (!sid) {
        continue;
      }
      const arr = groups.get(sid);
      if (arr) {
        arr.push(ev);
      } else {
        groups.set(sid, [ev]);
      }
    }
    const sessions: WebAnalyticsUserSession[] = [];
    for (const [sid, evs] of groups) {
      const sorted = [...evs].sort((a, b) =>
        a.timestamp.localeCompare(b.timestamp),
      );
      const start = sorted[0]?.timestamp ?? '';
      const end = sorted[sorted.length - 1]?.timestamp ?? '';
      sessions.push({
        sessionId: sid,
        startTime: start,
        endTime: end,
        durationSeconds: this.durationSeconds(start, end),
        eventCount: sorted.length,
        browser: this.topField(sorted, 'browser'),
        device: this.topField(sorted, 'device'),
        events: sorted.map((e) => ({
          timestamp: e.timestamp,
          eventType: e.eventType,
          path: e.path,
          referrer: e.referrer,
          browser: e.browser,
          os: e.os,
          device: e.device,
          extra: e.extra,
        })),
      });
    }
    sessions.sort((a, b) => b.startTime.localeCompare(a.startTime));
    return sessions;
  }

  private computeTopPaths(
    events: WebAnalyticsUserEventRow[],
  ): WebAnalyticsPathCount[] {
    const counts = new Map<string, number>();
    for (const ev of events) {
      const path = (ev.path ?? '').trim() || '(empty)';
      counts.set(path, (counts.get(path) ?? 0) + 1);
    }
    return [...counts.entries()]
      .map(([path, count]) => ({ path, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 20);
  }

  private durationSeconds(start: string, end: string): number {
    const s = Date.parse(start);
    const e = Date.parse(end);
    if (Number.isNaN(s) || Number.isNaN(e) || e < s) {
      return 0;
    }
    return Math.round((e - s) / 1000);
  }

  private topField(
    events: WebAnalyticsUserEventRow[],
    field: 'browser' | 'device',
  ): string {
    const counts = new Map<string, number>();
    for (const ev of events) {
      const v = (ev[field] ?? '').trim() || '(unknown)';
      counts.set(v, (counts.get(v) ?? 0) + 1);
    }
    let best = '';
    let bestCount = -1;
    for (const [k, v] of counts) {
      if (v > bestCount) {
        best = k;
        bestCount = v;
      }
    }
    return best;
  }

  private applyCharts(d: WebAnalyticsUserDetailData): void {
    const isDark = document.documentElement.classList.contains('theme-dark');
    const isCb = document.documentElement.classList.contains('colorblind-mode');
    const text = isDark ? '#e5e7eb' : '#374151';
    const grid = isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)';
    const c = getChartColors(isCb);
    const labels = d.eventsByDay.map((b) => b.date.slice(5));
    const counts = d.eventsByDay.map((b) => b.count);
    this.activityData.set({
      labels,
      datasets: [
        {
          label: 'Events',
          data: counts,
          fill: true,
          tension: 0.35,
          borderColor: c.info.border,
          backgroundColor: `rgba(70,166,180,${isDark ? 0.22 : 0.15})`,
        },
      ],
    });
    this.activityOptions.set({
      responsive: true,
      maintainAspectRatio: false,
      plugins: { legend: { labels: { color: text } } },
      scales: {
        x: { ticks: { color: text }, grid: { color: grid } },
        y: {
          beginAtZero: true,
          ticks: { color: text, precision: 0 },
          grid: { color: grid },
        },
      },
    });

    const rows = [...d.eventTypeBreakdown].sort((a, b) => b.count - a.count);
    const rev = [...rows].reverse();
    this.typeBarData.set({
      labels: rev.map((r) => r.eventType),
      datasets: [
        {
          label: 'Events',
          data: rev.map((r) => r.count),
          backgroundColor: `rgba(55,132,144,${isDark ? 0.45 : 0.35})`,
          borderWidth: 1,
          borderRadius: 4,
        },
      ],
    });
    this.typeBarOptions.set({
      indexAxis: 'y',
      responsive: true,
      maintainAspectRatio: false,
      plugins: { legend: { display: false } },
      scales: {
        x: {
          beginAtZero: true,
          ticks: { color: text, precision: 0 },
          grid: { color: grid },
        },
        y: { ticks: { color: text, font: { size: 11 } }, grid: { display: false } },
      },
    });
  }
}
